import { useMemo, useState } from 'react';

import { Stack, useMediaQuery, useTheme } from '@mui/system';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ActionBarAction } from '~components/actionBar/actionBarAction';
import ActionButton from '~components/buttons/actionButton';
import ActionButtonStyle from '~components/buttons/actionButtonStyle';
import DateRangeInput from '~components/datePickers/dateRangeInput';
import DeviceModelAutocomplete from '~components/inputFields/autocompletes/deviceModelAutocomplete';
import OrganisationsAutocomplete from '~components/inputFields/autocompletes/organisationsAutocomplete';
import ResourceGroupAutocomplete from '~components/inputFields/autocompletes/resourceGroupAutocomplete';
import DeviceStateDropdown from '~components/inputFields/deviceStateDropdown';
import InputField from '~components/inputFields/InputField';
import KeyTypesDropdown from '~components/inputFields/keyTypesDropdown';
import LockStateDropdown from '~components/inputFields/lockStateDropdown';
import SimpleRolesDropDown from '~components/inputFields/simpleRolesDropdown';
import VectorImage from '~components/media/vectorImage';
import { queryParameters } from '~constants';
import { DataGridToolBarFilterType, DeviceStateEnum, LockStateEnum, UserRoleEnum } from '~enums';
import KeyTypeEnum from '~enums/keyTypeEnum';
import useAuthorize from '~hooks/useAuthorize';
import { Dimens } from '~themes';

import ColumnFilterDrawer from './drawers/columnFilterDrawer';
import DataGridFiltersDrawer from './drawers/dataGridFiltersDrawer';
import ActionBarFilter from './interfaces/actionBarFilter';
import { ColDef } from './interfaces/colDef';

interface DataGridToolbarProps<T> {
	filters: ActionBarFilter[];
	actions?: ActionBarAction[];
	columns?: ColDef<T>[];
}

/**
 * When adding a new filter there's a few things you have to take care of:
 * 1. Create a new enum in the [DataGridToolBarFilterType.ts]
 * 2. Ensure there's a value it's mapped to in [queryParameters.ts]
 * 3. Create a useState for it.
 * 4. Make sure it's synced from the url [syncFiltersFromUrl = ()], with potential defaults.
 * 5. Make sure it's reset in [resetValues = ()]. This has to be in here so any other query params that aren't filters remain unaffected.
 * 6. Update the filtersMemo to include the new filter and the component to handle the filter.
 */
function DataGridToolbar<T>({ filters, actions = [], columns }: DataGridToolbarProps<T>) {
	const { t } = useTranslation();
	const theme = useTheme();
	const { authorize } = useAuthorize();
	const isSuperAdmin = authorize([UserRoleEnum.SuperAdmin]);
	const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
	const [manageColumnsOpen, setManageColumnsOpen] = useState(false);
	const [hasSetInitialValues, setHasSetInitialValues] = useState(false);
	const [searchParams, setSearchParams] = useSearchParams();

	const [searchValue, setSearchValue] = useState<string | null>('');
	const [deviceModelNameValue, setDeviceModelNameValue] = useState<string | null>(null);
	const [organisationIdValue, setOrganisationIdValue] = useState<string | null>(null);
	const [resourceGroupIdValue, setResourceGroupIdValue] = useState<string | null>(null);
	const [keyTypeValue, setKeyTypeValue] = useState<KeyTypeEnum | null>(null);
	const [userRoleValue, setUserRoleValue] = useState<UserRoleEnum | null>(null);
	const [startDateValue, setStartDateValue] = useState<string | null>('');
	const [endDateValue, setEndDateValue] = useState<string | null>('');
	const [lockStateValue, setLockStateValue] = useState<LockStateEnum | null>(null);
	const [deviceStateValue, setDeviceStateValue] = useState<DeviceStateEnum | null>(null);
	const [filterStateOnOpen, setFilterStateOnOpen] = useState<string>('');
	const [resetTrigger, setResetTrigger] = useState(0);

	const largerThanMd = useMediaQuery(theme.breakpoints.up('md'));
	const hasOnlySearch = filters.length == 1 && filters[0].type == DataGridToolBarFilterType.Search;

	// Url syncing
	const syncFiltersFromUrl = () => {
		const searchParams = new URLSearchParams(window.location.search);
		const dateRangeFilter = filters.find(
			(filter) => filter.type == DataGridToolBarFilterType.DateRange
		);

		setSearchValue(searchParams.get(queryParameters.SearchQuery));
		setDeviceModelNameValue(searchParams.get(queryParameters.DeviceModelName));
		setOrganisationIdValue(searchParams.get(queryParameters.OrganisationId));
		setResourceGroupIdValue(searchParams.get(queryParameters.ResourceGroupId));
		setKeyTypeValue(searchParams.get(queryParameters.KeyType) as KeyTypeEnum | null);
		setUserRoleValue(searchParams.get(queryParameters.Role) as UserRoleEnum | null);
		setStartDateValue(
			searchParams.get(queryParameters.StartDate) ??
				dateRangeFilter?.initialStartDate?.toISOString() ??
				dayjs().add(-7, 'day').startOf('day').toISOString()
		);
		setEndDateValue(
			searchParams.get(queryParameters.EndDate) ??
				dateRangeFilter?.initialEndDate?.toISOString() ??
				dayjs().endOf('day').toISOString()
		);
		setLockStateValue(searchParams.get(queryParameters.LockState) as LockStateEnum | null);
		setDeviceStateValue(searchParams.get(queryParameters.DeviceState) as DeviceStateEnum | null);
	};

	if (!hasSetInitialValues) {
		setHasSetInitialValues(true);
		syncFiltersFromUrl();
	}

	useMemo(() => {
		if (settingsMenuOpen) {
			setFilterStateOnOpen(window.location.search);
		}
	}, [settingsMenuOpen]);

	const userRoleFilers = useMemo(() => {
		return [
			UserRoleEnum.ClientAdmin,
			UserRoleEnum.User,
			...(isSuperAdmin ?
				[UserRoleEnum.SuperAdmin, UserRoleEnum.Manufacturing, UserRoleEnum.Support]
			:	[]),
		];
	}, []);

	const updateSearchParam = (
		updates:
			| { key: string; value: string | undefined | null }
			| { key: string; value: string | undefined | null }[]
	) => {
		setSearchParams((_) => {
			const next = new URLSearchParams(window.location.search);
			const list = Array.isArray(updates) ? updates : [updates];

			list.forEach(({ key, value }) => {
				if (!value) {
					next.delete(key);
				} else {
					next.set(key, value);
				}
			});

			return next.toString();
		});
	};

	const resetValues = () => {
		// Only delete our own filters, and leave other's in place, like OverwrittenClientId.
		const nextParams = new URLSearchParams(searchParams);
		nextParams.delete(queryParameters.SearchQuery);
		nextParams.delete(queryParameters.DeviceModelName);
		nextParams.delete(queryParameters.OrganisationId);
		nextParams.delete(queryParameters.ResourceGroupId);
		nextParams.delete(queryParameters.KeyType);
		nextParams.delete(queryParameters.Role);
		nextParams.delete(queryParameters.StartDate);
		nextParams.delete(queryParameters.EndDate);
		nextParams.delete(queryParameters.LockState);
		nextParams.delete(queryParameters.DeviceState);
		setSearchParams(nextParams);

		const start = dayjs().add(-7, 'day').startOf('day').toISOString();
		const end = dayjs().endOf('day').toISOString();
		setSearchValue('');
		setDeviceModelNameValue(null);
		setOrganisationIdValue(null);
		setResourceGroupIdValue(null);
		setKeyTypeValue(null);
		setUserRoleValue(null);
		setStartDateValue(start);
		setEndDateValue(end);
		setLockStateValue(null);
		setDeviceStateValue(null);

		setResetTrigger((prev) => prev + 1);

		filters.forEach((filter) => {
			if (filter.type == DataGridToolBarFilterType.Search) {
				filter.onUpdated?.('');
			} else {
				filter.onUpdated?.(undefined);
			}
		});
	};

	const filtersMemo = useMemo(() => {
		return filters.map((filter) => {
			switch (filter.type) {
				case DataGridToolBarFilterType.Search:
					return (
						<InputField
							key={'search'}
							value={filter.value ?? searchValue ?? ''}
							placeholder={t('search', { ns: 'actions' })}
							StartIcon='icon/search'
							onChange={(newValue) => {
								updateSearchParam({ key: queryParameters.SearchQuery, value: newValue });
								setSearchValue(newValue);
								filter.onUpdated?.(newValue);
							}}
							sx={{
								width: {
									xs: '100%',
									md: '200px',
								},
							}}
						/>
					);
				case DataGridToolBarFilterType.DeviceModel:
					return (
						<DeviceModelAutocomplete
							key={'DeviceModelAutocomplete'}
							label={largerThanMd ? '' : t('device.fields.model', { ns: 'entities' })}
							value={filter.value ?? deviceModelNameValue}
							onValueChanged={(newValue) => {
								updateSearchParam({ key: queryParameters.DeviceModelName, value: newValue?.name });
								setDeviceModelNameValue(newValue?.name ?? null);
								filter.onUpdated?.(newValue);
							}}
							sx={{
								width: {
									xs: '100%',
									md: '200px',
								},
							}}
						/>
					);
				case DataGridToolBarFilterType.Organisation:
					return (
						<OrganisationsAutocomplete
							value={filter.value ?? organisationIdValue}
							label={largerThanMd ? '' : t('organisation.labels.single', { ns: 'entities' })}
							key={'OrganisationsAutocomplete'}
							disableClearable={false}
							onValueChanged={(newValue) => {
								updateSearchParam({
									key: queryParameters.OrganisationId,
									value: newValue?.id ?? '',
								});
								setOrganisationIdValue(newValue?.id ?? null);
								filter.onUpdated?.(newValue);
							}}
							sx={{
								width: {
									xs: '100%',
									md: '200px',
								},
							}}
						/>
					);
				case DataGridToolBarFilterType.ResourceGroup:
					return (
						<ResourceGroupAutocomplete
							key={'ResourceGroupAutocomplete'}
							label={largerThanMd ? '' : t('resourceGroup.labels.single', { ns: 'entities' })}
							filterOrganisationId={filter.organisationId}
							isInternal={filter.isInternal}
							value={filter.value ?? resourceGroupIdValue}
							onValueChanged={(newValue) => {
								updateSearchParam({ key: queryParameters.ResourceGroupId, value: newValue?.id });
								setResourceGroupIdValue(newValue?.id ?? null);
								filter.onUpdated?.(newValue);
							}}
							sx={{
								width: {
									xs: '100%',
									md: '200px',
								},
							}}
						/>
					);
				case DataGridToolBarFilterType.KeyType:
					return (
						<KeyTypesDropdown
							key='KeyType'
							label={largerThanMd ? '' : t('key.labels.keyType', { ns: 'entities' })}
							selectedValue={filter.value ?? keyTypeValue}
							onValueChanged={(newValue) => {
								updateSearchParam({ key: queryParameters.KeyType, value: newValue });
								setKeyTypeValue(newValue as KeyTypeEnum);
								filter.onUpdated?.(newValue);
							}}
						/>
					);
				case DataGridToolBarFilterType.UserRole:
					return (
						<SimpleRolesDropDown
							key='UserRole'
							label={largerThanMd ? '' : t('user.fields.role', { ns: 'entities' })}
							selectedValue={filter.value ?? userRoleValue}
							selectableRoles={userRoleFilers}
							placeholder={t('user.fields.role', { ns: 'entities' })}
							disableClearable={false}
							onValueChanged={(newValue) => {
								updateSearchParam({ key: queryParameters.Role, value: newValue });
								setUserRoleValue(newValue as UserRoleEnum);
								filter.onUpdated?.(newValue);
							}}
							sx={{
								width: {
									xs: '100%',
									md: '200px',
								},
							}}
						/>
					);
				case DataGridToolBarFilterType.DateRange:
					return (
						<DateRangeInput
							key='DateRange'
							initialStartDate={filter.initialStartDate ?? dayjs(startDateValue)}
							initialEndDate={filter.initialEndDate ?? dayjs(endDateValue)}
							onRangeUpdated={(start, end) => {
								updateSearchParam([
									{ key: queryParameters.StartDate, value: start.toISOString() },
									{ key: queryParameters.EndDate, value: end.toISOString() },
								]);
								setStartDateValue(start.toISOString());
								setEndDateValue(end.toISOString());
								filter.onUpdated?.({ start: start, end: end });
							}}
							sx={{
								width: {
									xs: '100%',
									md: '300px',
								},
							}}
							resetTrigger={resetTrigger}
						/>
					);
				case DataGridToolBarFilterType.LockState:
					return (
						<LockStateDropdown
							key='LockState'
							label={largerThanMd ? '' : t('device.labels.lockState', { ns: 'entities' })}
							selectedValue={filter.value ?? lockStateValue}
							onValueChanged={(newValue) => {
								updateSearchParam({ key: queryParameters.LockState, value: newValue });
								setLockStateValue(newValue as LockStateEnum);
								filter.onUpdated?.(newValue);
							}}
							sx={{
								width: {
									xs: '100%',
									md: '200px',
								},
							}}
						/>
					);
				case DataGridToolBarFilterType.DeviceState:
					return (
						<DeviceStateDropdown
							key='DeviceState'
							label={largerThanMd ? '' : t('device.labels.deviceState', { ns: 'entities' })}
							selectedValue={filter.value ?? deviceStateValue}
							onValueChanged={(newValue) => {
								updateSearchParam({ key: queryParameters.DeviceState, value: newValue });
								setDeviceStateValue(newValue as DeviceStateEnum);
								filter.onUpdated?.(newValue);
							}}
							sx={{
								width: {
									xs: '100%',
									md: '200px',
								},
							}}
						/>
					);
			}
		});
	}, [
		searchValue,
		deviceModelNameValue,
		organisationIdValue,
		resourceGroupIdValue,
		keyTypeValue,
		userRoleValue,
		startDateValue,
		endDateValue,
		lockStateValue,
		deviceStateValue,
		filters,
		largerThanMd,
		t,
	]);

	return (
		<>
			<Stack direction={{ xs: 'column', sm: 'row' }} gap={Dimens.spacingMd}>
				<Stack
					direction={'row'}
					gap={Dimens.spacingMd}
					justifyContent='space-between'
					sx={{ flex: 1 }}
				>
					<Stack
						direction={'row'}
						gap={Dimens.spacingLg}
						sx={{
							flex: 1,
							flexWrap: {
								sx: 'nowrap',
								md: 'wrap',
							},
						}}
					>
						{/*Only show search on smaller screens*/}
						{largerThanMd ? filtersMemo : filtersMemo[0]}
						{(largerThanMd || hasOnlySearch) && (
							<ActionButton
								key={'actionBarFilterMenuClear'}
								style={ActionButtonStyle.Text}
								onClick={resetValues}
								label={
									largerThanMd ?
										t('clearFilters', { ns: 'actions' })
									:	t('clear', { ns: 'actions' })
								}
							/>
						)}
					</Stack>
					<Stack direction={'row'} gap={Dimens.spacingMd}>
						{!hasOnlySearch && !largerThanMd && (
							<ActionButton
								key={'actionBarFilterMenuToggle'}
								startIcon={<VectorImage name='icon/sliders' />}
								style={ActionButtonStyle.Bordered}
								onClick={() => setSettingsMenuOpen(true)}
								sx={{
									// Should have some size variations on the buttons maybe. But to have it look
									// nice with the filter inputs, it's 40x40
									width: '40px',
									height: '40px',
								}}
							/>
						)}
					</Stack>
				</Stack>
				<Stack
					direction='row'
					justifyContent='end'
					gap={Dimens.spacingMd}
					sx={{ height: 'fit-content' }}
				>
					{actions.map((actionButton) => {
						return (
							<ActionButton
								key={actionButton.id}
								label={actionButton.label}
								variant={actionButton.variant}
								style={actionButton.style}
								startIcon={actionButton.icon}
								onClick={actionButton.onClick}
								disabled={actionButton.disabled}
								loading={actionButton.loading}
							/>
						);
					})}

					{columns && (
						<ActionButton
							key='columns-toggle'
							onClick={() => setManageColumnsOpen(true)}
							style={ActionButtonStyle.Bordered}
							startIcon={<VectorImage name='icon/columns' />}
						/>
					)}
				</Stack>
			</Stack>
			<DataGridFiltersDrawer
				isOpen={settingsMenuOpen}
				filters={filtersMemo}
				onApply={() => setSettingsMenuOpen(false)}
				onClose={() => {
					setSearchParams(new URLSearchParams(filterStateOnOpen));
					syncFiltersFromUrl();
					setSettingsMenuOpen(false);
				}}
				onReset={() => resetValues()}
			/>
			<ColumnFilterDrawer<T>
				isOpen={manageColumnsOpen}
				columns={columns ?? []}
				onClose={() => setManageColumnsOpen(false)}
			/>
		</>
	);
}

export default DataGridToolbar;
